'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { College } from '@/lib/services/collegeService';
import CollegeCard from './CollegeCard';
import { ArrowUpDown, ChevronLeft, ChevronRight, LayoutGrid, List, X, GitCompare, SlidersHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { FilterState } from './FilterSidebar';

type SortOption = 'nirf-rank' | 'fees-low' | 'fees-high' | 'rating' | 'name';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'nirf-rank', label: 'NIRF Ranking' },
  { value: 'fees-low', label: 'Fees: Low to High' },
  { value: 'fees-high', label: 'Fees: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'name', label: 'Name A–Z' },
];

const ITEMS_PER_PAGE = 6;

interface CollegeListingContentProps {
  filters?: FilterState;
  onOpenMobileFilters?: () => void;
  colleges: College[];
  loading?: boolean;
}

export default function CollegeListingContent({ filters, onOpenMobileFilters, colleges, loading }: CollegeListingContentProps) {
  const [sortBy, setSortBy] = useState<SortOption>('nirf-rank');
  const [currentPage, setCurrentPage] = useState(1);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredAndSorted = useMemo(() => {
    let list = [...colleges];

    if (filters) {
      if (filters.streams.length > 0) {
        list = list.filter((c) => c.streams.some((s) => filters.streams.includes(s)));
      }
      if (filters.states.length > 0) {
        list = list.filter((c) => filters.states.includes(c.state));
      }
      if (filters.cities && filters.cities.length > 0) {
        list = list.filter((c) => filters.cities!.includes(c.location));
      }
      if (filters.ownership.length > 0) {
        list = list.filter((c) => filters.ownership.includes(c.ownership));
      }
      if (filters.accreditation.length > 0) {
        list = list.filter((c) => filters.accreditation.includes(c.accreditation));
      }
      if (filters.feesMax < 5000000) {
        list = list.filter((c) => c.totalFees <= filters.feesMax);
      }
      if (filters.nirfRankMax < 200) {
        list = list.filter((c) => c.nirfRank !== null && c.nirfRank <= filters.nirfRankMax);
      }
      if (filters.hostel) {
        list = list.filter((c) => c.hostel);
      }
      if (filters.scholarships) {
        list = list.filter((c) => c.scholarships);
      }
    }

    switch (sortBy) {
      case 'nirf-rank':
        list.sort((a, b) => (a.nirfRank ?? 999) - (b.nirfRank ?? 999));
        break;
      case 'fees-low':
        list.sort((a, b) => a.totalFees - b.totalFees);
        break;
      case 'fees-high':
        list.sort((a, b) => b.totalFees - a.totalFees);
        break;
      case 'rating':
        list.sort((a, b) => b.rating - a.rating);
        break;
      case 'name':
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return list;
  }, [filters, sortBy, colleges]);

  const totalPages = Math.ceil(filteredAndSorted.length / ITEMS_PER_PAGE);
  const paginatedColleges = filteredAndSorted.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSortChange = (val: SortOption) => {
    setSortBy(val);
    setCurrentPage(1);
  };

  const toggleCompare = (id: string) => {
    if (compareList.includes(id)) {
      setCompareList(compareList.filter((c) => c !== id));
    } else {
      if (compareList.length >= 3) {
        toast.error('You can compare up to 3 colleges at a time.');
        return;
      }
      setCompareList([...compareList, id]);
      toast.success('College added to comparison.');
    }
  };

  const getCompareColleges = () => colleges.filter((c) => compareList.includes(c.id));

  const activeFilterChips: string[] = filters
    ? [
        ...filters.streams,
        ...filters.states,
        ...filters.ownership,
        ...filters.accreditation,
        ...(filters.hostel ? ['Hostel'] : []),
        ...(filters.scholarships ? ['Scholarships'] : []),
        ...(filters.feesMax < 5000000 ? [`Fees ≤ ₹${(filters.feesMax / 100000).toFixed(0)}L`] : []),
        ...(filters.nirfRankMax < 200 ? [`NIRF Top ${filters.nirfRankMax}`] : []),
      ]
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={32} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading colleges...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground font-tabular">
            <span className="text-primary">{filteredAndSorted.length}</span> colleges found
          </span>
          <button
            className="lg:hidden flex items-center gap-1.5 text-sm text-muted-foreground border border-border rounded-lg px-3 py-1.5 hover:border-primary hover:text-primary transition-colors"
            onClick={onOpenMobileFilters}
          >
            <SlidersHorizontal size={14} />
            Filters
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <ArrowUpDown size={14} className="text-muted-foreground shrink-0" />
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value as SortOption)}
                className="select-base py-1.5 pl-3 pr-8 text-sm appearance-none cursor-pointer min-w-[180px]"
              >
                {sortOptions.map((opt) => (
                  <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">▾</span>
            </div>
          </div>

          <div className="flex items-center gap-1 border border-border rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="Grid view"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="List view"
            >
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilterChips.map((chip) => (
            <span key={`active-filter-${chip}`} className="filter-chip">
              {chip}
            </span>
          ))}
        </div>
      )}

      {paginatedColleges.length > 0 ? (
        <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
          {paginatedColleges.map((college) => (
            <CollegeCard
              key={`college-card-${college.id}`}
              college={college}
              isCompared={compareList.includes(college.id)}
              onToggleCompare={() => toggleCompare(college.id)}
              viewMode={viewMode}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-card rounded-2xl border border-border">
          <SlidersHorizontal size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="font-bold text-foreground">No colleges match your filters</p>
          <p className="text-sm text-muted-foreground mt-1">Try adjusting or resetting your filters</p>
        </div>
      )}

      {filteredAndSorted.length > ITEMS_PER_PAGE && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground font-tabular">
            Showing{' '}
            <span className="font-semibold text-foreground">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSorted.length)}
            </span>{' '}
            of <span className="font-semibold text-foreground">{filteredAndSorted.length}</span> colleges
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={`page-${page}`}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-semibold transition-colors ${
                  page === currentPage
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border hover:bg-muted text-foreground'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {compareList.length >= 2 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-secondary text-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4 border border-white/10">
          <GitCompare size={20} className="text-primary" />
          <span className="font-semibold text-sm">{compareList.length} colleges selected</span>
          <a
            href={`/compare-colleges?ids=${compareList.join(',')}`}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-xl text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            Compare Now
          </a>
          <button onClick={() => setCompareList([])} className="text-white/60 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}